/**
 * Schema detail — fields editor, preview (`/{slug}/preview`) and the FieldSource
 * mapping (`/{slug}/field-mapping`).
 *
 * Saving PUTs the whole `CustomSchemaDefinition` under the `schema` alias; the
 * endpoint upserts and bumps `version`, so the first save against a code default
 * is what creates the customization. LOS canonical rows are read-only —
 * everything editable is disabled for them rather than failing at the API.
 *
 * Field shape (SchemaFieldDefinition): name, field_type, description, required,
 * default, method (Azure CU extract | generate | classify), enum_values,
 * items_type, items_schema, properties. The grid edits the scalar half; nested
 * `properties` / `items_schema` round-trip untouched.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import { useSnackbar } from "notistack";

import { AdminGrid } from "../shared/adminGrid";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminSchemasApi } from "../../../api/adminPlatformApi";
import { AdminCard, AdminEmpty } from "../shared/AdminScreenShell";
import PillSwitcher from "../shared/PillSwitcher";

const FIELD_TYPES = [
  "string",
  "number",
  "integer",
  "boolean",
  "date",
  "time",
  "array",
  "object",
];
const CU_METHODS = ["", "extract", "generate", "classify"];

const VIEWS = [
  { key: "fields", label: "Fields" },
  { key: "mapping", label: "LOS field mapping" },
  { key: "preview", label: "JSON schema" },
];

export default function SchemaDetailDrawer({ schema, onClose, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState("fields");
  const [fields, setFields] = useState([]);
  const [meta, setMeta] = useState({ name: "", description: "", doc_group: "" });
  const [dirty, setDirty] = useState(false);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const slug = schema?._id;
  const readOnly = schema?._source === "los_canonical";

  useEffect(() => {
    if (!schema) return;
    setView("fields");
    setFields((schema.fields || []).map((f, i) => ({ ...f, __i: i })));
    setMeta({
      name: schema.name || "",
      description: schema.description || "",
      doc_group: schema.doc_group || "",
    });
    setDirty(false);
    setMapping(null);
    setPreview(null);
  }, [schema]);

  useEffect(() => {
    if (!slug || view !== "mapping" || mapping) return;
    adminSchemasApi
      .fieldMapping(slug)
      .then(setMapping)
      .catch(() => setMapping({ error: true }));
  }, [slug, view, mapping]);

  useEffect(() => {
    if (!slug || view !== "preview" || preview) return;
    adminSchemasApi
      .preview(slug)
      .then(setPreview)
      .catch(() => setPreview({ error: true }));
  }, [slug, view, preview]);

  const save = useCallback(() => {
    if (!slug || readOnly) return;
    setBusy(true);
    const definition = {
      schema_id: slug,
      name: meta.name,
      description: meta.description || null,
      doc_type_slug: slug,
      doc_group: meta.doc_group || null,
      fields: fields.map(({ __i, ...rest }) => rest),
      skill_markdown: schema?.skill_markdown ?? null,
    };
    adminSchemasApi
      .update(slug, definition)
      .then((out) => {
        enqueueSnackbar(`Saved as v${out?.version}`, { variant: "success" });
        setDirty(false);
        onChanged?.();
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Save failed", {
          variant: "error",
        }),
      )
      .finally(() => setBusy(false));
  }, [slug, readOnly, meta, fields, schema, enqueueSnackbar, onChanged]);

  const removeCustomization = useCallback(() => {
    if (!slug) return;
    if (
      !window.confirm(
        `Delete the customization for ${slug}? The code-default schema takes over again — the class itself is not removed.`,
      )
    )
      return;
    setBusy(true);
    adminSchemasApi
      .remove(slug)
      .then(() => {
        enqueueSnackbar("Customization removed", { variant: "success" });
        onChanged?.();
        onClose?.();
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Delete failed", {
          variant: "error",
        }),
      )
      .finally(() => setBusy(false));
  }, [slug, enqueueSnackbar, onChanged, onClose]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Field",
        field: "name",
        minWidth: 200,
        flex: 1,
        editable: !readOnly,
      },
      {
        headerName: "Type",
        field: "field_type",
        width: 130,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: FIELD_TYPES },
      },
      {
        headerName: "Required",
        field: "required",
        width: 110,
        editable: !readOnly,
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor",
      },
      {
        headerName: "CU method",
        field: "method",
        width: 140,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: CU_METHODS },
        valueFormatter: (p) => p.value || "auto",
      },
      {
        headerName: "Enum values",
        colId: "enum_values",
        minWidth: 180,
        flex: 1,
        valueGetter: (p) => (p.data?.enum_values || []).join(", "),
        valueSetter: (p) => {
          p.data.enum_values = String(p.newValue || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
          return true;
        },
        editable: !readOnly,
      },
      {
        headerName: "Description",
        field: "description",
        minWidth: 260,
        flex: 2,
        editable: !readOnly,
      },
      {
        headerName: "",
        colId: "remove",
        width: 90,
        sortable: false,
        cellRenderer: (p) =>
          readOnly ? null : (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setFields((prev) => prev.filter((f) => f.__i !== p.data.__i));
                setDirty(true);
              }}
            >
              Remove
            </Button>
          ),
      },
    ],
    [readOnly],
  );

  return (
    <Drawer
      anchor="right"
      open={!!schema}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 900 }, bgcolor: "#f8fafc" } }}
    >
      {!schema ? null : (
        <Box sx={{ p: 2.5 }}>
          <Flx ac jb g={1.5} sx={{ mb: 2 }} wrap>
            <Flx column g={0.5}>
              <Flx ac g={1} wrap>
                <Txt sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>
                  {schema.name}
                </Txt>
                <Chip
                  size="small"
                  label={schema._source?.replace("_", " ")}
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
                {schema.version ? (
                  <Chip
                    size="small"
                    label={`v${schema.version}`}
                    sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                  />
                ) : null}
              </Flx>
              <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>{slug}</Txt>
            </Flx>
            <Flx ac g={1}>
              <PillSwitcher items={VIEWS} value={view} onChange={setView} />
              <IconButton onClick={onClose} size="small">
                <CloseRounded fontSize="small" />
              </IconButton>
            </Flx>
          </Flx>

          {readOnly ? (
            <Txt
              sx={{
                fontSize: 12.5,
                color: "#92400e",
                display: "block",
                mb: 2,
              }}
            >
              LOS canonical writeback model — read-only. Extraction targets are
              customized instead; this class is the destination they write into.
            </Txt>
          ) : null}

          {view === "fields" ? (
            <>
              <AdminCard title="Identity">
                <Flx g={2} wrap>
                  <TextField
                    size="small"
                    label="Name"
                    value={meta.name}
                    disabled={readOnly}
                    onChange={(e) => {
                      setMeta((p) => ({ ...p, name: e.target.value }));
                      setDirty(true);
                    }}
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label="Doc group"
                    value={meta.doc_group}
                    disabled={readOnly}
                    onChange={(e) => {
                      setMeta((p) => ({ ...p, doc_group: e.target.value }));
                      setDirty(true);
                    }}
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label="Description"
                    value={meta.description}
                    disabled={readOnly}
                    onChange={(e) => {
                      setMeta((p) => ({ ...p, description: e.target.value }));
                      setDirty(true);
                    }}
                    sx={{ flex: "1 1 100%" }}
                  />
                </Flx>
              </AdminCard>

              <AdminCard
                title={`Fields (${fields.length})`}
                endContent={
                  readOnly ? null : (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddRounded />}
                      onClick={() => {
                        setFields((prev) => [
                          ...prev,
                          {
                            __i: Date.now(),
                            name: "",
                            field_type: "string",
                            required: true,
                            description: "",
                          },
                        ]);
                        setDirty(true);
                      }}
                    >
                      Add field
                    </Button>
                  )
                }
                dense
              >
                <AdminGrid rowCount={(fields || []).length} maxRows={10}>
                  <AgGridReact
                    rowData={fields}
                    columnDefs={columnDefs}
                    defaultColDef={{
                      sortable: false,
                      resizable: true,
                      suppressHeaderMenuButton: true,
                      suppressHeaderFilterButton: true,
                    }}
                    getRowId={(p) => String(p.data.__i)}
                    onCellValueChanged={() => setDirty(true)}
                    stopEditingWhenCellsLoseFocus
                  />
                </AdminGrid>
              </AdminCard>

              {readOnly ? null : (
                <Flx ac g={1.5} wrap>
                  <Button
                    variant="contained"
                    onClick={save}
                    disabled={busy || !dirty}
                  >
                    {schema._source === "customized"
                      ? "Save customization"
                      : "Create customization"}
                  </Button>
                  {schema._source === "customized" ? (
                    <Button
                      variant="outlined"
                      onClick={removeCustomization}
                      disabled={busy}
                    >
                      Remove customization
                    </Button>
                  ) : null}
                </Flx>
              )}
            </>
          ) : null}

          {view === "mapping" ? (
            <AdminCard title="FieldSource mapping">
              {!mapping ? (
                <AdminEmpty>Loading…</AdminEmpty>
              ) : mapping.error ? (
                <AdminEmpty>No mapping available for this schema.</AdminEmpty>
              ) : (
                <JsonBlock value={mapping} />
              )}
            </AdminCard>
          ) : null}

          {view === "preview" ? (
            <>
              <AdminCard title="JSON schema">
                {!preview ? (
                  <AdminEmpty>Loading…</AdminEmpty>
                ) : preview.error ? (
                  <AdminEmpty>Preview unavailable.</AdminEmpty>
                ) : (
                  <JsonBlock value={preview.json_schema} />
                )}
              </AdminCard>
              {preview?.cu_fields ? (
                <AdminCard title="Azure CU field list">
                  <JsonBlock value={preview.cu_fields} />
                </AdminCard>
              ) : null}
            </>
          ) : null}
        </Box>
      )}
    </Drawer>
  );
}

SchemaDetailDrawer.propTypes = {
  schema: PropTypes.object,
  onClose: PropTypes.func,
  onChanged: PropTypes.func,
};

const JsonBlock = ({ value }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 2,
      maxHeight: 520,
      overflow: "auto",
      bgcolor: "#0f172a",
      color: "#e2e8f0",
      borderRadius: "8px",
      fontSize: 11.5,
      lineHeight: 1.6,
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);
